<template>
  <div :class="generatorMainSectionClass">
    <div>

      <div class="drugstore-stage-action-buttons">
        <div
            class="d-none d-sm-inline-block btn btn-sm btn-success shadow-sm"
            @click="openCreateKeyModal()"
        >
          <i class="fas fa-plus-circle"/>
          Utwórz / zastąp klucz API
        </div>
      </div>

      <div class="row">
        <div>
          <CommonTable2
              :table="tableData"
              :key="tableData.data"
              rowIdentifier="drugstoreId"
              @onItemAction="handleItemAction($event)"
              @onSort="sortBy($event)"
              :currentPage="currentPage"
              :totalRows="commonModule.getDrugstoreApiKeysDataTotal"
              :totalPages="commonModule.getDrugstoreApiKeysPagesCount"
              :showPaginationTop="true"
              :showPaginationBottom="true"
              @onPageChange="currentPage = $event"
              :sortColumn="sortColumn"
              :sortDirection="sortingDirection"
              :showMassActions="false"
              :showRowActions="true"
              :multiselect="true"
              :showCheckboxColumn="true"
              :stickyScrollbarBottom="0"
          />
        </div>

        <div class="p-4">
        </div>
      </div>
    </div>
  </div>

  <!-- Proposal Key Modal (RAW key display + copy + commit/cancel) -->
  <b-modal
      v-model="rawKeyModalDisplayed"
      id="raw-key-modal"
      scrollable
      title="Klucz API wygenerowany"
      title-class="font-18 fw-bold"
      hide-footer
      size="lg"
      @hidden="onRawKeyModalHidden()"
  >
    <div class="alert alert-warning d-flex align-items-center mb-3">
      <i class="fas fa-exclamation-triangle me-3 fs-4"></i>
      <div>
        <b>Uwaga:</b> Ten klucz zostanie wyświetlony tylko raz. Skopiuj go i przechowuj w bezpiecznym miejscu.
        Po zapisaniu nie będzie możliwe jego ponowne wyświetlenie.
      </div>
    </div>

    <div class="mb-3">
      <label class="form-label fw-bold">Klucz API (RAW):</label>
      <div class="input-group">
        <input
            type="text"
            class="form-control font-monospace"
            :value="generatedRawKey"
            readonly
        />
        <button
            class="btn btn-outline-secondary"
            type="button"
            @click="copyRawKey()"
        >
          <i class="fas fa-copy"></i> Kopiuj
        </button>
      </div>
      <div v-if="rawKeyCopied" class="text-success mt-1">
        <i class="fas fa-check"></i> Skopiowano do schowka
      </div>

      <div v-if="proposalExpiresAt" class="text-muted mt-2 small">
        Propozycja ważna do: <b>{{ proposalExpiresAt }}</b>
      </div>
    </div>

    <div class="d-flex justify-content-end align-items-center gap-2 mt-3 pt-3 border-top">
      <button class="btn btn-outline-secondary px-4 me-auto" @click="cancelProposalAndClose()">
        Anuluj
      </button>
      <button class="btn btn-success px-4" @click="commitProposalAndClose()">
        <i class="fas fa-check me-1"></i> OK (Zapisz klucz)
      </button>
    </div>
  </b-modal>

  <!-- Create / Replace Key Modal -->
  <b-modal
      v-model="createKeyModalDisplayed"
      id="create-key-modal"
      scrollable
      title="Utwórz / zastąp klucz API"
      title-class="font-18 fw-bold"
      hide-footer
      size="lg"
  >
    <div class="alert alert-warning d-flex align-items-center mb-3">
      <i class="fas fa-exclamation-triangle me-3 fs-4"></i>
      <div>
        Jeśli apteka posiada już klucz API, zostanie on <b>trwale zastąpiony</b> nowym kluczem.
        Stary klucz przestanie działać natychmiast.
      </div>
    </div>

    <div class="mb-3">
      <label class="form-label">Apteka (ID apteki):</label>
      <select v-model="createKeyDrugstoreId" class="form-select">
        <option value="">-- Wybierz aptekę --</option>
        <option
            v-for="drugstore in availableDrugstores"
            :key="drugstore.drugstoreClientId"
            :value="drugstore.drugstoreClientId"
        >
          {{ drugstore.drugstoreClientId }} – {{ drugstore.name }}
        </option>
      </select>
    </div>

    <!-- Mode selection: generate vs manual -->
    <div class="mb-3">
      <label class="form-label">Sposób utworzenia klucza:</label>
      <select v-model="createKeyMode" class="form-select">
        <option value="GENERATE">Wygeneruj automatycznie</option>
        <option value="MANUAL">Wklej ręcznie</option>
      </select>
      <div class="text-muted small mt-1">
        W trybie "Wygeneruj" klucz zostanie pokazany jednorazowo do skopiowania, a dopiero potem zapisany (hash).
      </div>
    </div>

    <div v-if="createKeyMode === 'MANUAL'" class="mb-3">
      <label class="form-label fw-bold">Wklej klucz API (RAW):</label>
      <input
          v-model="manualRawKey"
          type="text"
          class="form-control font-monospace"
          placeholder="Wklej tutaj klucz API..."
      />
      <div class="text-muted small mt-1">
        Po zapisaniu klucz zostanie zahashowany i nie będzie możliwe jego podejrzenie.
      </div>
    </div>

    <div class="mb-3">
      <div class="form-check form-switch">
        <input
            class="form-check-input"
            type="checkbox"
            id="createKeyEnabled"
            v-model="createKeyEnabled"
        />
        <label class="form-check-label" for="createKeyEnabled">Klucz aktywny od razu</label>
      </div>
    </div>

    <div class="d-flex justify-content-end align-items-center gap-2 mt-4 pt-3 border-top">
      <button class="btn btn-outline-secondary px-4 me-auto" @click="createKeyModalDisplayed = false">
        Anuluj
      </button>

      <button
          class="btn btn-success px-4"
          :disabled="isCreateKeySubmitDisabled"
          @click="confirmCreateOrReplaceKey()"
      >
        <i class="fas fa-key me-1"></i> OK
      </button>
    </div>
  </b-modal>

</template>

<script>
import Swal from "sweetalert2";
import {BModal} from "bootstrap-vue-next";
import {useSimplepharmAuthModule} from "@/state/pinia/simplepharmAuthModule";
import {useCommonAppModule} from "@/state/pinia/commonAppModule";
import {useLayoutStore} from "@/state/pinia";
import CommonTable2 from "@/components/widgets/CommonTable2.vue";
import {log} from "@/state/pinia/log";
import dateFormat from "dateformat";

export default {
  name: "DrugstoreApiKeysList",
  components: {CommonTable2, BModal},
  data() {
    return {
      apiKeysList: [],
      availableDrugstores: [],
      buttonsEnabled: true,
      refreshTable: true,
      sortingDirection: "ASC",
      sortColumn: "drugstoreId",
      currentPage: 1,
      tableData: {},

      // proposal/raw modal
      rawKeyModalDisplayed: false,
      generatedRawKey: "",
      rawKeyCopied: false,
      currentProposalId: null,
      proposalExpiresAt: "",
      proposalCommitted: false,

      // create modal
      createKeyModalDisplayed: false,
      createKeyDrugstoreId: "",
      createKeyEnabled: true,

      // NEW: create mode + manual input
      createKeyMode: "GENERATE", // GENERATE | MANUAL
      manualRawKey: "",

      availableHeaders: [
        {key: "drugstoreId", text: "ID Apteki", type: "text", sortable: true, width: "160px"},
        {key: "drugstoreName", text: "Nazwa apteki", type: "text", sortable: false, width: "260px"},
        {key: "enabled", text: "Aktywny", type: "text", sortable: false, width: "120px"},
        {key: "createdAt", text: "Utworzono", type: "text", sortable: false, width: "180px"},
        {key: "revokedAt", text: "Unieważniono", type: "text", sortable: false, width: "180px"},
        {key: "actions", text: "Akcje", type: "actions", sortable: false, width: "220px"},
      ],
    };
  },
  computed: {
    simplepharmAuth() {
      return useSimplepharmAuthModule();
    },
    commonModule() {
      return useCommonAppModule();
    },
    layoutStore() {
      return useLayoutStore();
    },
    layout: {
      get() {
        return this.layoutStore.layoutType;
      },
      set(layout) {
        this.layoutStore.changeLayoutType(layout);
      }
    },
    generatorMainSectionClass() {
      if (this.layout === 'vertical') {
        return 'row package-orders-generator-site';
      } else {
        return 'package-orders-generator-site';
      }
    },

    isCreateKeySubmitDisabled() {
      if (!this.createKeyDrugstoreId) {
        return true;
      }
      if (this.createKeyMode === "MANUAL") {
        return !this.manualRawKey || this.manualRawKey.trim().length === 0;
      }
      return false;
    },
  },
  methods: {
    sortBy(sortingKeyValue) {
      if (this.sortColumn === sortingKeyValue) {
        this.sortingDirection = this.sortingDirection === "ASC" ? "DESC" : "ASC";
      } else {
        this.sortColumn = sortingKeyValue;
        this.sortingDirection = "DESC";
      }
      this.currentPage = 1;
      this.refreshApiKeysList();
    },

    openCreateKeyModal() {
      this.createKeyDrugstoreId = "";
      this.createKeyEnabled = true;
      this.createKeyMode = "GENERATE";
      this.manualRawKey = "";
      this.createKeyModalDisplayed = true;
    },

    /**
     * MAIN create/replace flow:
     * - MANUAL: save immediately (hash in backend) -> refresh list
     * - GENERATE: proposal -> show RAW modal -> commit on OK, cancel on Cancel/X
     */
    confirmCreateOrReplaceKey() {
      const drugstoreId = this.createKeyDrugstoreId;
      const existingKey = this.apiKeysList.find(k => k.drugstoreId === drugstoreId);

      let warningText = "";
      if (this.createKeyMode === "MANUAL") {
        warningText = existingKey
            ? `Apteka ${drugstoreId} posiada już klucz API. Zostanie on trwale zastąpiony manualnie wklejonym kluczem. Czy na pewno chcesz kontynuować?`
            : `Zostanie zapisany manualnie wklejony klucz API dla apteki ${drugstoreId}.`;
      } else {
        warningText = existingKey
            ? `Apteka ${drugstoreId} posiada już klucz API. Zostanie on trwale zastąpiony. Najpierw zobaczysz nowy klucz do skopiowania, a dopiero potem go zapiszesz. Kontynuować?`
            : `Zostanie wygenerowany nowy klucz API dla apteki ${drugstoreId}. Najpierw zobaczysz klucz do skopiowania, a dopiero potem go zapiszesz.`;
      }

      this.createKeyModalDisplayed = false;

      Swal.fire({
        title: "Potwierdzenie",
        text: warningText,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#34c38f",
        cancelButtonColor: "#f46a6a",
        cancelButtonText: "Anuluj",
        confirmButtonText: "Tak, kontynuuj",
        customClass: {container: "swal-highest"},
      }).then((result) => {
        if (!result.isConfirmed) {
          return;
        }

        if (this.createKeyMode === "MANUAL") {
          this.saveManualKey(drugstoreId);
        } else {
          this.proposeKeyAndShowModal(drugstoreId, this.createKeyEnabled);
        }
      });
    },

    saveManualKey(drugstoreId) {
      const raw = (this.manualRawKey || "").trim();
      this.manualRawKey = "";

      this.commonModule.httpSetManualDrugstoreApiKey(drugstoreId, raw, this.createKeyEnabled)
          .then((response) => {
            if (response && response.status === 200) {
              Swal.fire({
                title: "Zapisano",
                text: "Manualny klucz API został zapisany (zapisano tylko hash; klucza nie da się potem podejrzeć).",
                icon: "success",
                confirmButtonText: "OK",
                customClass: {container: "swal-highest"},
              });
              this.refreshApiKeysList();
            } else {
              Swal.fire({
                title: "Błąd",
                text: "Nie udało się zapisać manualnego klucza API.",
                icon: "error",
                confirmButtonText: "OK",
                customClass: {container: "swal-highest"},
              });
            }
          });
    },

    /**
     * Proposal (STEP1) then open RAW modal
     */
    proposeKeyAndShowModal(drugstoreId, enabled) {
      this.proposalCommitted = false;
      this.currentProposalId = null;
      this.generatedRawKey = "";
      this.rawKeyCopied = false;
      this.proposalExpiresAt = "";

      this.commonModule.httpProposeDrugstoreApiKey(drugstoreId, enabled)
          .then((response) => {
            if (response && response.status === 200) {
              const dto = (response.data && (response.data.payload || response.data.data)) || null;
              if (dto && dto.rawKey && dto.proposalId) {
                this.currentProposalId = dto.proposalId;
                this.generatedRawKey = dto.rawKey;
                this.proposalExpiresAt = dto.expiresAt || "";
                this.rawKeyModalDisplayed = true;
              } else {
                Swal.fire({
                  title: "Błąd",
                  text: "Backend nie zwrócił propozycji klucza (proposalId/rawKey).",
                  icon: "error",
                  confirmButtonText: "OK",
                  customClass: {container: "swal-highest"},
                });
              }
            } else {
              Swal.fire({
                title: "Błąd",
                text: "Nie udało się wygenerować propozycji klucza API.",
                icon: "error",
                confirmButtonText: "OK",
                customClass: {container: "swal-highest"},
              });
            }
          });
    },

    commitProposalAndClose() {
      if (!this.currentProposalId) {
        this.rawKeyModalDisplayed = false;
        return;
      }

      this.commonModule.httpCommitDrugstoreApiKeyProposal(this.currentProposalId)
          .then((response) => {
            if (response && response.status === 200) {
              this.proposalCommitted = true;
              this.rawKeyModalDisplayed = false;
              this.currentProposalId = null;
              this.generatedRawKey = "";
              this.rawKeyCopied = false;
              this.proposalExpiresAt = "";
              this.refreshApiKeysList();
            } else {
              Swal.fire({
                title: "Błąd",
                text: "Nie udało się zapisać klucza (commit proposal).",
                icon: "error",
                confirmButtonText: "OK",
                customClass: {container: "swal-highest"},
              });
            }
          });
    },

    cancelProposalAndClose() {
      const pid = this.currentProposalId;
      this.rawKeyModalDisplayed = false;

      // best-effort cancel
      if (pid) {
        this.commonModule.httpCancelDrugstoreApiKeyProposal(pid).then(() => {
          this.currentProposalId = null;
          this.generatedRawKey = "";
          this.rawKeyCopied = false;
          this.proposalExpiresAt = "";
        });
      }
    },

    onRawKeyModalHidden() {
      // If modal was closed by X / escape, cancel proposal best-effort (unless already committed)
      if (!this.proposalCommitted && this.currentProposalId) {
        const pid = this.currentProposalId;
        this.commonModule.httpCancelDrugstoreApiKeyProposal(pid).then(() => {
          this.currentProposalId = null;
          this.generatedRawKey = "";
          this.rawKeyCopied = false;
          this.proposalExpiresAt = "";
        });
      }
      this.proposalCommitted = false;
    },

    rotateKey(drugstoreId) {
      Swal.fire({
        title: "Rotacja klucza API",
        text: `Czy na pewno chcesz zrotować klucz API dla apteki ${drugstoreId}? Stary klucz przestanie działać natychmiast.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#34c38f",
        cancelButtonColor: "#f46a6a",
        cancelButtonText: "Anuluj",
        confirmButtonText: "Tak, zrotuj klucz",
        customClass: {container: "swal-highest"},
      }).then((result) => {
        if (result.isConfirmed) {
          // rotate == replace from DB perspective -> proposal + commit
          const current = this.apiKeysList.find(k => k.drugstoreId === drugstoreId);
          const enabled = current ? !!current.enabled : true;
          this.proposeKeyAndShowModal(drugstoreId, enabled);
        }
      });
    },

    toggleKeyEnabled(drugstoreId, currentEnabled) {
      const newEnabled = !currentEnabled;
      const actionText = newEnabled ? "aktywować" : "dezaktywować";
      Swal.fire({
        title: `Zmiana stanu klucza API`,
        text: `Czy na pewno chcesz ${actionText} klucz API dla apteki ${drugstoreId}?`,
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#34c38f",
        cancelButtonColor: "#f46a6a",
        cancelButtonText: "Anuluj",
        confirmButtonText: "Tak",
        customClass: {container: "swal-highest"},
      }).then((result) => {
        if (result.isConfirmed) {
          this.commonModule.httpSetDrugstoreApiKeyEnabled(drugstoreId, newEnabled)
              .then(() => {
                this.refreshApiKeysList();
              });
        }
      });
    },

    revokeKey(drugstoreId) {
      Swal.fire({
        title: "Unieważnienie klucza API",
        html: `<b>UWAGA:</b> Unieważnienie klucza API dla apteki <b>${drugstoreId}</b> jest <b>nieodwracalne</b>.<br>Apteka straci dostęp do systemu natychmiast.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#f46a6a",
        cancelButtonColor: "#74788d",
        cancelButtonText: "Anuluj",
        confirmButtonText: "Tak, unieważnij klucz",
        customClass: {container: "swal-highest"},
      }).then((result) => {
        if (result.isConfirmed) {
          this.commonModule.httpRevokeDrugstoreApiKey(drugstoreId)
              .then(() => {
                this.refreshApiKeysList();
              });
        }
      });
    },

    handleItemAction(event) {
      log.log(event);
      const drugstoreId = event.itemData.drugstoreId;
      const item = this.apiKeysList.find(k => k.drugstoreId === drugstoreId);
      if (event.action.action === "rotate-key") {
        this.rotateKey(drugstoreId);
      } else if (event.action.action === "toggle-enabled") {
        this.toggleKeyEnabled(drugstoreId, item ? item.enabled : true);
      } else if (event.action.action === "revoke-key") {
        this.revokeKey(drugstoreId);
      }
    },

    copyRawKey() {
      if (this.generatedRawKey) {
        navigator.clipboard.writeText(this.generatedRawKey).then(() => {
          this.rawKeyCopied = true;
        });
      }
    },

    refreshApiKeysList() {
      const requestPayload = {
        currentPage: this.currentPage,
        sortingKey: this.sortColumn,
        sortingDirection: this.sortingDirection,
      };

      this.refreshTable = false;
      this.commonModule.httpGetDrugstoreApiKeysPaging(requestPayload).then((response) => {
        if (response && response.status === 200) {
          this.apiKeysList = this.commonModule.getDrugstoreApiKeys || [];
        }

        const drugstoresNameMap = this.commonModule.getDrugstoresNameMap || {};
        const rows = this.apiKeysList.map(item => ({
          drugstoreId: item.drugstoreId,
          drugstoreName: drugstoresNameMap[item.drugstoreId] || item.drugstoreId,
          enabled: item.enabled ? "Aktywny" : "Nieaktywny",
          createdAt: item.createdAt ? new Date(Math.floor(item.createdAt * 1000)) : null,
          revokedAt: item.revokedAt ? new Date(Math.floor(item.revokedAt * 1000)) : null,
          actions: [
            {
              icon: "fas fa-sync-alt",
              class: "btn btn-warning table-actions-button",
              text: "",
              action: "rotate-key",
              enabled: this.buttonsEnabled,
              title: "Rotuj klucz"
            },
            {
              icon: item.enabled ? "fas fa-toggle-on" : "fas fa-toggle-off",
              class: item.enabled
                  ? "btn btn-secondary table-actions-button"
                  : "btn btn-success table-actions-button",
              text: "",
              action: "toggle-enabled",
              enabled: this.buttonsEnabled,
              title: item.enabled ? "Dezaktywuj" : "Aktywuj"
            },
            {
              icon: "fas fa-ban",
              class: "btn btn-danger table-actions-button",
              text: "",
              action: "revoke-key",
              enabled: this.buttonsEnabled && !item.revokedAt,
              title: "Unieważnij klucz"
            },
          ],
        }));

        this.tableData["headers"] = this.availableHeaders;
        this.tableData["massActions"] = [];
        this.tableData["data"] = rows;

        this.refreshTable = true;
      });
    },

    loadAvailableDrugstores() {
      const requestPayload = {
        currentPage: 1,
        sortingKey: "drugstoreClientId",
        sortingDirection: "ASC",
        searchAndFilterRequest: {searchPhrase: "", searchKey: "name"},
      };
      this.commonModule.httpGetDrugstoresPaging(requestPayload).then(() => {
        this.availableDrugstores = this.commonModule.getDrugstores || [];
      });
    },

  },
  watch: {
    currentPage: {
      handler() {
        this.refreshApiKeysList();
      },
      deep: true,
    },
  },

  created() {
    this.simplepharmAuth.refreshToken().then(() => {
      this.commonModule.httpGetWorkingProfile().then(() => {
        this.refreshApiKeysList();
        this.loadAvailableDrugstores();
        this.commonModule.httpGetDrugstoresNameMap();
      });
    });
  },
};
</script>

<style scoped>
.table-actions-button {
  font-size: 0.8em !important;
  margin-left: 5px;
  margin-right: 5px;
}

.drugstore-stage-action-buttons {
  text-align: right;
  margin-right: 4px;
  margin-bottom: 16px;
  margin-top: 16px;
}

.package-orders-generator-site {
  min-height: 100px;
}
</style>